<script setup lang="ts">
import type { TimeRangeController } from '@/composables/useTimeRange'
import type { ResolvedRange } from '@/lib/charts/timeRange'
import type { ChartSeries } from '@/lib/charts/types'
import { CurveType } from '@unovis/ts'
import { VisArea, VisAxis, VisCrosshair, VisFreeBrush, VisLine, VisScatter, VisTooltip, VisXYContainer } from '@unovis/vue'
import { useElementSize } from '@vueuse/core'
import { format } from 'date-fns'
import { computed, useTemplateRef } from 'vue'
import { injectTimeRangeOptional } from '@/composables/useTimeRange'
import { toAbsolute } from '@/lib/charts/timeRange'

const props = withDefaults(defineProps<{
  series: ChartSeries[]
  resolved: ResolvedRange
  height?: number
  /** Compact variant: no axes, brush or markers, for inline metric panels. */
  sparkline?: boolean
  /** Stack the series into a single filled area (e.g. health states over time). */
  stacked?: boolean
  /** Enable Grafana-style drag-to-zoom; defaults on for full charts. */
  zoomable?: boolean
  /** Range controller to drive on zoom; falls back to the page-provided one. */
  controller?: TimeRangeController
  valueFormat?: (value: number) => string
}>(), { height: 220, sparkline: false, stacked: false })

type Row = Record<string, number | null>

// Linear interpolation reads as honest sampled data — the default monotone
// curve rounds every ramp into a blob, which looks nothing like Grafana.
const CURVE = CurveType.Linear

// Key under which a stacked band stores its cumulative top edge, so a line and
// markers can trace the boundary the filled area actually draws to.
const topKey = (id: string) => `__top:${id}`

// Buckets where every series is null carry a baseline marker so we can trace a
// faint dashed line through them — distinguishing "no data" from a real zero.
const NO_DATA_KEY = '__nodata'

const wrapper = useTemplateRef<HTMLElement | null>('wrapper')
const { width } = useElementSize(wrapper)
// Guard against a 0 measurement on first paint (e.g. mounting inside <Deferred>):
// a zero-width container gives the brush a zero-width extent, so it can't select.
const chartWidth = computed(() => Math.max(width.value, 320))

// Merge every series onto a shared, sorted x-axis so the crosshair and scales
// see one dataset; missing buckets stay null and break the line into gaps.
const rows = computed<Row[]>(() => {
  const xs = new Set<number>()
  for (const entry of props.series) {
    for (const point of entry.points)
      xs.add(point.x)
  }

  const lookups = props.series.map(entry => new Map(entry.points.map(point => [point.x, point.y])))

  return [...xs].sort((a, b) => a - b).map((x) => {
    const row: Row = { x }
    let cumulative = 0
    let anyPresent = false
    props.series.forEach((entry, index) => {
      const value = lookups[index].get(x) ?? null
      row[entry.id] = value
      if (value != null)
        anyPresent = true
      cumulative += value ?? 0
      row[topKey(entry.id)] = cumulative
    })
    row[NO_DATA_KEY] = anyPresent ? null : 0
    return row
  })
})

// Only worth drawing the gap baseline when there is at least one real and one
// missing bucket — an all-empty series shows the panel's empty state instead.
const hasGaps = computed(() =>
  rows.value.some(row => row[NO_DATA_KEY] != null) && rows.value.some(row => row[NO_DATA_KEY] == null),
)
const noDataOf = (row: Row) => row[NO_DATA_KEY]

const xDomain = computed<[number, number]>(() => [props.resolved.from, props.resolved.to])
const crosshairColor = computed(() => props.series[0]?.color ?? 'var(--ui-primary)')
// Ring drawn around each marker so it reads as a distinct point on the band.
const markerRing = 'var(--ui-bg)'

// Drag-to-zoom pushes the new window into a range controller: an explicit one
// when given, otherwise the page-scoped controller provided by an ancestor.
// `inject` must run here in setup — calling it lazily inside a computed returns
// null because there is no active component instance at that point.
const injectedController = injectTimeRangeOptional()
const controller = computed(() => props.controller ?? injectedController)
const zoomEnabled = computed(() => (props.zoomable ?? true) && controller.value !== null)

// Markers are all-or-nothing: only when zoomed in far enough that every sample
// owns this much horizontal room do we draw them. Below that they'd smear into
// a dense band, so we show no points at all rather than a crowded lesser set.
const MARKER_MIN_SPACING_PX = 28
const showPoints = computed(() =>
  !props.sparkline && rows.value.length > 1 && chartWidth.value / rows.value.length >= MARKER_MIN_SPACING_PX,
)

const x = (row: Row) => row.x as number
const valueOf = (id: string) => (row: Row) => row[id]
const topOf = (id: string) => (row: Row) => row[topKey(id)]
const stackedAccessors = computed(() => props.series.map(entry => valueOf(entry.id)))
const stackedColors = computed(() => props.series.map(entry => entry.color))

const formatValue = computed(() =>
  props.valueFormat ?? ((value: number) => new Intl.NumberFormat('en', { maximumFractionDigits: 2 }).format(value)),
)

const spanDays = computed(() => (props.resolved.to - props.resolved.from) / 86_400_000)

function formatTick(value: number): string {
  const date = new Date(value)
  if (spanDays.value <= 2)
    return format(date, 'HH:mm')
  if (spanDays.value <= 60)
    return format(date, 'd MMM')
  return format(date, 'MMM yy')
}

function tooltip(row: Row): string {
  const stamp = format(new Date(row.x as number), spanDays.value <= 2 ? 'd MMM HH:mm' : 'd MMM yyyy')
  const lines = props.series
    .filter(entry => row[entry.id] != null)
    .map(entry =>
      `<div class="flex items-center gap-2"><span class="size-2 rounded-full" style="background:${entry.color}"></span>`
      + `<span>${entry.label}</span><span class="ml-auto font-medium">${formatValue.value(row[entry.id] as number)}</span></div>`,
    )
  return `<div class="text-dimmed mb-1 text-xs">${stamp}</div><div class="flex flex-col gap-1">${lines.join('')}</div>`
}

// The free brush reports its selection in x-domain units (epoch ms). On release
// we pin the range to that window; the brush auto-hides afterwards.
function onBrushEnd(selection: [number, number] | undefined, _event: unknown, userDriven: boolean): void {
  if (!userDriven || !selection)
    return
  // The brush can report coordinates beyond the visible window when data is
  // sparse, which would zoom into an empty "future" slice. Clamp to the domain
  // and never past the present moment.
  const ceiling = Math.min(props.resolved.to, Date.now())
  const from = Math.max(props.resolved.from, Math.min(selection[0], selection[1]))
  const to = Math.min(ceiling, Math.max(selection[0], selection[1]))
  if (to - from < 1)
    return
  controller.value?.setRange(toAbsolute({ from, to }))
}

function zoomOut(): void {
  controller.value?.zoomOut()
}
</script>

<template>
  <div ref="wrapper" class="time-series-chart w-full" @dblclick="zoomEnabled && zoomOut()">
    <VisXYContainer
      :data="rows"
      :x-domain="xDomain"
      :height="height"
      :width="chartWidth"
      :padding="sparkline ? { top: 8, bottom: 4 } : { top: 12, bottom: 24, left: 48, right: 12 }"
    >
      <VisLine
        v-if="hasGaps"
        :x="x"
        :y="noDataOf"
        color="var(--ui-text-dimmed)"
        :line-width="1"
        :line-dash-array="[2, 4]"
        :curve-type="CURVE"
      />
      <template v-if="stacked">
        <VisArea :x="x" :y="stackedAccessors" :color="stackedColors" :opacity="0.55" :curve-type="CURVE" />
        <template v-for="entry in series" :key="entry.id">
          <VisLine :x="x" :y="topOf(entry.id)" :color="entry.color" :line-width="1.5" :curve-type="CURVE" />
          <VisScatter v-if="showPoints" :x="x" :y="topOf(entry.id)" :color="entry.color" :size="5" :stroke-color="markerRing" :stroke-width="1.5" />
        </template>
      </template>
      <template v-else>
        <template v-for="entry in series" :key="entry.id">
          <VisArea v-if="entry.type === 'area'" :x="x" :y="valueOf(entry.id)" :color="entry.color" :opacity="0.1" :curve-type="CURVE" />
          <VisLine :x="x" :y="valueOf(entry.id)" :color="entry.color" :line-width="2" :curve-type="CURVE" />
          <VisScatter v-if="showPoints" :x="x" :y="valueOf(entry.id)" :color="entry.color" :size="5" :stroke-color="markerRing" :stroke-width="1.5" />
        </template>
      </template>

      <VisAxis
        v-if="!sparkline"
        type="x"
        :x="x"
        :tick-format="formatTick"
        :grid-line="false"
        :num-ticks="6"
      />
      <VisAxis
        v-if="!sparkline && zoomEnabled"
        type="y"
        :tick-format="(value: number) => formatValue(value)"
        :grid-line="true"
        :domain-line="false"
        :num-ticks="4"
      />

      <VisCrosshair :color="crosshairColor" :template="tooltip" />
      <VisTooltip />

      <VisFreeBrush
        v-if="!sparkline"
        mode="x"
        :auto-hide="true"
        :selection-min-length="1"
        :on-brush-end="onBrushEnd"
      />
    </VisXYContainer>
  </div>
</template>

<style scoped>
.time-series-chart {
  --vis-crosshair-line-stroke-color: var(--ui-text-dimmed);
  --vis-crosshair-line-stroke-width: 1px;
  --vis-crosshair-circle-stroke-color: var(--ui-bg);
  --vis-axis-tick-color: transparent;
  --vis-axis-tick-label-color: var(--ui-text-dimmed);
  --vis-axis-grid-color: color-mix(in oklab, var(--ui-border) 70%, transparent);
  --vis-axis-grid-line-width: 1px;
  --vis-tooltip-background-color: var(--ui-bg);
  --vis-tooltip-border-color: var(--ui-border);
  --vis-tooltip-text-color: var(--ui-text-highlighted);
  --vis-tooltip-padding: 0.5rem 0.75rem;
  --vis-free-brush-selection-fill-color: var(--ui-primary);
  --vis-free-brush-selection-fill-opacity: 0.12;
  --vis-free-brush-selection-stroke-color: var(--ui-primary);
}

.time-series-chart :deep(.selection) {
  stroke-opacity: 1;
}
</style>
